import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import type { RequiredEnvironmentValue } from '../types/RequiredEnvironmentValue';
import { getEnvironmentValue } from './getEnvironmentValue';
import { detectAppType } from './detectAppType';
import { getEnvironment } from './getEnvironment';
import { getRequiredEnvironmentKeys } from './getRequiredEnvironmentKeys';

/**
 * @group Functions
 * @summary <blockquote>Gets a required environment variable value with automatic app type and environment detection.</blockquote>
 * 
 * This function automatically detects the app type (web/mobile) and environment (development/preview/production)
 * internally, eliminating the need for callers to pass these parameters. It validates that the requested key
 * is in the required registry for the detected app/environment combination before retrieving the value.
 * 
 * @param key - The environment variable key to retrieve (must be a valid RequiredEnvironmentValue)
 * @returns Result containing the environment variable value or an AppError if missing/not required
 * 
 * @example
 * ```typescript
 * import { getRequiredEnvironmentValue, ENVIRONMENT_VALUES } from '@shared/utils';
 * 
 * // Auto-detects app type and environment internally
 * const doxyUrlResult = getRequiredEnvironmentValue(ENVIRONMENT_VALUES.DOXY.NEXT_PUBLIC_DOXY_URL);
 * 
 * doxyUrlResult.match({
 *   ok: (url) => {
 *     // Use the URL - guaranteed to be present
 *     console.log(`Doxy URL: ${url}`);
 *   },
 *   err: (error) => {
 *     // Handle error - show toast/Alert based on platform
 *     console.error(`Missing required env var: ${error.errorMessage}`);
 *   }
 * });
 * ```
 * 
 * **Key Features:**
 * - Auto-detects app type (web/mobile) using `getJavascriptEnvironment()`
 * - Auto-detects environment (development/preview/production) using `getEnvironment()`
 * - Validates key is in required registry for detected app/env
 * - Returns Result pattern (never throws)
 * 
 * **Error Handling:**
 * - Returns error if app type or environment detection fails
 * - Returns error if variable is not in required registry for detected app/env
 * - Returns error if variable is missing (even if in registry)
 * - Component should handle error with appropriate UI (toast for web, Alert for mobile)
 * - Never use hardcoded fallbacks - always handle errors properly
 */
export function getRequiredEnvironmentValue(
  key: RequiredEnvironmentValue
): Result<string, AppError> {
  // Step 1: Auto-detect app type
  const appTypeResult = detectAppType();
  if (appTypeResult.isErr) {
    return err({
      errorCode: 'APP_TYPE_DETECTION_FAILED',
      errorMessage: 'Failed to detect app type for required environment variable validation'
    });
  }
  const appType = appTypeResult.value;

  // Step 2: Auto-detect environment
  const envResult = getEnvironment();
  if (envResult.isErr) {
    return err({
      errorCode: 'ENVIRONMENT_DETECTION_FAILED',
      errorMessage: 'Failed to detect environment for required environment variable validation'
    });
  }
  const environment = envResult.value;

  // Step 3: Verify key is in required registry for detected app/env
  const requiredKeysResult = getRequiredEnvironmentKeys(appType, environment);
  if (requiredKeysResult.isErr) {
    return err(requiredKeysResult.getFail());
  }
  const requiredKeys = requiredKeysResult.value;
  const isRequired = requiredKeys.includes(key as any);
  
  if (!isRequired) {
    return err({
      errorCode: 'VARIABLE_NOT_REQUIRED',
      errorMessage: `Environment variable '${key}' is not required for ${appType}/${environment}. Use getEnvironmentValue() for optional variables.`
    });
  }

  // Step 4: Get value using getEnvironmentValue (with required=true)
  const valueResult = getEnvironmentValue(key, true);
  
  if (valueResult.isErr) {
    const error = valueResult.getFail();
    return err({
      errorCode: error.errorCode,
      errorMessage: error.errorMessage
    });
  }

  return ok(valueResult.value);
}

